import { Box, Button, Grid } from '@mui/material';

import ItineraryDateRangeSelector from '~/components/Form/ItineraryDateRangeSelector';
import ItineraryLocationSelector from '~/components/Form/ItineraryLocationSelector';
import TravelersOptionsSelector from '~/components/Form/TravelersOptionsSelector';
import { FlightSearchFormValidationMessages, FlightSearchParams, ItineraryPackage, Place } from '~/types';
import { FlightType, TripTravellers } from '~/types/common';
import { getSearchValidationMessages } from '~/utils/flight-itinerary-validation';

type Props = {
  searchParams: FlightSearchParams;
  validationMode: boolean;
  validationMessages: FlightSearchFormValidationMessages;
  onSearchParamsChange: (params: FlightSearchParams) => void;
  onValidationMessagesChange: (messages: FlightSearchFormValidationMessages) => void;
  onNext?: () => void;
  nextLabel?: string;
  dateConstraints?: ItineraryPackage['dateConstraints'];
};

function FlightItineraryTripSearchStep({
  searchParams,
  validationMode,
  validationMessages,
  onSearchParamsChange,
  onValidationMessagesChange,
  onNext,
  nextLabel = 'Next: Passenger Details',
  dateConstraints,
}: Props) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const addDays = (date: Date, days: number) => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };
  const addMonths = (date: Date, months: number) => {
    const result = new Date(date);
    result.setMonth(result.getMonth() + months);
    return result;
  };
  const departureMinDate = dateConstraints
    ? addDays(today, dateConstraints.departure.minDaysFromToday)
    : today;
  const departureMaxDate = dateConstraints ? addMonths(today, dateConstraints.departure.maxMonthsFromToday) : undefined;
  const returnMaxDate = dateConstraints ? addMonths(today, dateConstraints.return.maxMonthsFromToday) : undefined;
  const handleFlightTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchParamsChange({ ...searchParams, type: event.target.value as FlightType });
  };

  const handleTravelClassChange = (value: string) => onSearchParamsChange({ ...searchParams, travelClass: value });
  const handleAdultPassangersChange = (_: any, value: number | null) => onSearchParamsChange({ ...searchParams, passengers: { ...searchParams.passengers, adults: value || 0 } as TripTravellers });
  const handleChildrenPassangersChange = (_: any, value: number | null) => onSearchParamsChange({ ...searchParams, passengers: { ...searchParams.passengers, children: value || 0 } as TripTravellers });
  const handleInfantPassangersChange = (_: any, value: number | null) => onSearchParamsChange({ ...searchParams, passengers: { ...searchParams.passengers, infants: value || 0 } as TripTravellers });

  const handleFromLocationChange = (_: React.SyntheticEvent<Element, Event>, value: Place | null | string) => {
    const next = { ...searchParams, from: (typeof value === 'string' ? null : value) as Place | null };
    onSearchParamsChange(next);
    onValidationMessagesChange(getSearchValidationMessages(next, dateConstraints));
  };

  const handleToLocationChange = (_: React.SyntheticEvent<Element, Event>, value: Place | null | string) => {
    const next = { ...searchParams, to: (typeof value === 'string' ? null : value) as Place | null };
    onSearchParamsChange(next);
    onValidationMessagesChange(getSearchValidationMessages(next, dateConstraints));
  };

  const handleDatesChange = (departureDate: Date | null, returnDate: Date | null) => {
    const next = { ...searchParams, departureDate, returnDate };
    onSearchParamsChange(next);
    onValidationMessagesChange(getSearchValidationMessages(next, dateConstraints));
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TravelersOptionsSelector
            onAdultPassangersChange={handleAdultPassangersChange}
            onChildrenPassangersChange={handleChildrenPassangersChange}
            onFlightTypeChange={handleFlightTypeChange}
            onInfantPassangersChange={handleInfantPassangersChange}
            onTravelClassChange={handleTravelClassChange}
            tripOptions={{ type: searchParams.type, passengers: searchParams.passengers, travelClass: searchParams.travelClass }}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <ItineraryLocationSelector errorHelperText={validationMode ? validationMessages.from : undefined} label="From" location={searchParams.from} name="itinerary-from" onLocationChange={handleFromLocationChange} placeholder="From airport" required />
        </Grid>
        <Grid item xs={12} md={6}>
          <ItineraryLocationSelector errorHelperText={validationMode ? validationMessages.to : undefined} label="To" location={searchParams.to} name="itinerary-to" onLocationChange={handleToLocationChange} placeholder="To airport" required />
        </Grid>
        <Grid item xs={12}>
          <ItineraryDateRangeSelector
            departureDate={searchParams.departureDate}
            departureErrors={validationMode ? validationMessages.departure : undefined}
            departureLabel={searchParams.type === 'oneway' ? '(One Way) Departure' : 'Departure'}
            departureMinDate={departureMinDate}
            departureMaxDate={departureMaxDate}
            onDatesChange={handleDatesChange}
            returnDate={searchParams.returnDate ?? null}
            returnErrors={validationMode ? validationMessages.return : undefined}
            returnLabel="Return"
            returnMaxDate={returnMaxDate}
            returnMinDaysAfterDeparture={dateConstraints?.return.minDaysAfterDeparture ?? 0}
            singleDateMode={searchParams.type === 'oneway'}
          />
        </Grid>
        {onNext ? (
          <Grid item xs={12}>
            <Button fullWidth variant="contained" sx={{ maxWidth: { md: 220 } }} onClick={onNext}>{nextLabel}</Button>
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}

export default FlightItineraryTripSearchStep;
